import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { TranslationDrawer } from '@/features/translations/TranslationDrawer';
import { useTranslations } from '@/queries/translations';
import { useApiError } from '@/lib/useApiError';
import {
  translationStatusSchema,
  translationSubjectKindSchema,
  type TranslationListFilters,
  type TranslationStatus,
  type TranslationSubjectKind,
  type TranslationTask,
} from '@/schemas/admin/translations';

const searchSchema = z.object({
  status: translationStatusSchema.optional(),
  subjectKind: translationSubjectKindSchema.optional(),
  assignedTo: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  selected: z.string().optional(),
});

const STATUS_TABS: TranslationStatus[] = ['missing', 'drafted', 'reviewed', 'published'];
const SUBJECT_KINDS: TranslationSubjectKind[] = ['post', 'story', 'event', 'group', 'announcement'];
const LIMIT_OPTIONS = [10, 25, 50, 100];

export const Route = createFileRoute('/_authed/translations')({
  validateSearch: searchSchema,
  component: TranslationsScreen,
});

function TranslationsScreen() {
  const { t } = useTranslation('translations');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const resolveError = useApiError();

  const filters: TranslationListFilters = {
    ...(search.status ? { status: search.status } : {}),
    ...(search.subjectKind ? { subject_kind: search.subjectKind } : {}),
    ...(search.assignedTo ? { assigned_to: search.assignedTo } : {}),
    ...(search.limit ? { limit: search.limit } : { limit: 25 }),
  };

  const list = useTranslations(filters);

  const selectedTask = React.useMemo(
    () => (search.selected ? list.data?.find((r) => r.id === search.selected) ?? null : null),
    [list.data, search.selected]
  );

  function setSearchPatch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({
      to: '/translations',
      search: (prev) => {
        const next: Record<string, unknown> = { ...prev };
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '') delete next[k];
          else next[k] = v;
        }
        return next;
      },
      replace: true,
    });
  }

  const columns: readonly DataColumn<TranslationTask>[] = [
    {
      id: 'subject',
      header: t('table.subject'),
      cell: (r) => (
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
            {t(`kinds.${r.subject_kind}`)}
          </span>
          <span dir="ltr" className="font-mono text-xs">
            {r.subject_id.slice(0, 8)}…
          </span>
        </div>
      ),
    },
    { id: 'field', header: t('table.field'), cell: (r) => <span dir="ltr" className="font-mono text-xs">{r.field}</span>, sortBy: (r) => r.field },
    {
      id: 'locale',
      header: t('table.locale'),
      cell: (r) => <Badge tone="neutral">{r.locale.toUpperCase()}</Badge>,
      sortBy: (r) => r.locale,
    },
    {
      id: 'status',
      header: t('table.status'),
      cell: (r) => {
        const tone =
          r.status === 'published'
            ? 'success'
            : r.status === 'reviewed'
              ? 'accent'
              : r.status === 'drafted'
                ? 'info'
                : 'warn';
        return <Badge tone={tone}>{t(`states.${r.status}`)}</Badge>;
      },
      sortBy: (r) => r.status,
    },
    {
      id: 'assignee',
      header: t('table.assignee'),
      cell: (r) =>
        r.assigned_to ? (
          <span dir="ltr" className="font-mono text-xs">
            {r.assigned_to.slice(0, 8)}…
          </span>
        ) : (
          <span className="text-xs text-[color:var(--color-fg-muted)]">—</span>
        ),
    },
    {
      id: 'updated',
      header: t('table.updated'),
      cell: (r) => <DateTime value={r.updated_at} />,
      sortBy: (r) => (r.updated_at ? new Date(r.updated_at) : null),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
      </header>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div role="tablist" aria-label={t('filters.status')} className="flex flex-wrap gap-2">
            {[undefined, ...STATUS_TABS].map((s) => {
              const selected = (search.status ?? undefined) === s;
              return (
                <button
                  key={s ?? 'all'}
                  role="tab"
                  type="button"
                  aria-selected={selected}
                  onClick={() => setSearchPatch({ status: s })}
                  className={`rounded-[var(--radius-pill)] px-3 py-1 text-xs font-semibold ${
                    selected
                      ? 'bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]'
                      : 'bg-[color:var(--color-surface-2)] text-[color:var(--color-fg-muted)]'
                  }`}
                >
                  {s ? t(`states.${s}`) : t('filters.all')}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="trans-kind">{t('filters.subjectKind')}</Label>
              <select
                id="trans-kind"
                dir="ltr"
                value={search.subjectKind ?? ''}
                onChange={(e) => setSearchPatch({ subjectKind: (e.target.value || undefined) as TranslationSubjectKind | undefined })}
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm"
              >
                <option value="">{t('filters.all')}</option>
                {SUBJECT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`kinds.${k}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="trans-assignee-filter">{t('filters.assignedTo')}</Label>
              <Input
                id="trans-assignee-filter"
                dir="ltr"
                value={search.assignedTo ?? ''}
                onChange={(e) => setSearchPatch({ assignedTo: e.target.value || undefined })}
                placeholder={t('filters.anyone')}
                className="font-mono"
              />
            </div>
            <div>
              <Label htmlFor="trans-limit">{t('filters.limit')}</Label>
              <select
                id="trans-limit"
                dir="ltr"
                value={String(filters.limit ?? 25)}
                onChange={(e) => setSearchPatch({ limit: Number(e.target.value) })}
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm"
              >
                {LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setSearchPatch({
                  status: undefined,
                  subjectKind: undefined,
                  assignedTo: undefined,
                  limit: undefined,
                })
              }
            >
              {t('filters.clear')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<TranslationTask>
        data={list.data ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        error={list.error ? resolveError(list.error) : null}
        empty={{ title: t('empty'), body: t('emptyBody') }}
        onRowClick={(r) => setSearchPatch({ selected: r.id })}
        initialSort={{ id: 'updated', direction: 'desc' }}
      />

      <TranslationDrawer
        task={selectedTask}
        open={Boolean(selectedTask)}
        onOpenChange={(o) => {
          if (!o) setSearchPatch({ selected: undefined });
        }}
      />
    </div>
  );
}
